import Link from '@amzn/awsui-components-react/polaris/link';
import {
    AWSAccountAccessModal,
    formatString,
    getStringList,
    useLazyRequest,
} from '@amzn/event-engine-js-utils';
import { AWSError } from 'aws-sdk/lib/error';
import { useNotification } from 'hooks';
import React, {
    ComponentProps,
    FC,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { EEEvents, ErrorMessage } from 'services';
import { i18nStrings } from 'utils/constants';

import { links } from '../../../constants';
import LinkAsync from './LinkAsync';

export interface TeamCredentialsLinkProps {
    participantId: string;
    accessibleRegions?: string[];
    deploymentRegions?: string[];
}

const TeamCredentialsLink: FC<TeamCredentialsLinkProps> = ({
    participantId,
    accessibleRegions,
    deploymentRegions,
}) => {
    const [roleConfigs, setRoleConfigs] = useState<
        ComponentProps<typeof AWSAccountAccessModal>['roleConfigs']
    >();
    const { showNotification, clearNotification } = useNotification();
    const [
        getParticipantTeamCredentials,
        {
            data: participantTeamCredentialsData,
            isLoading: participantTeamCredentialsLoading,
            isError: participantTeamCredentialsError,
        },
    ] = useLazyRequest<typeof EEEvents.getParticipantTeamCredentials, AWSError>(
        EEEvents.getParticipantTeamCredentials,
        {}
    );
    const getTeamCredentials = useCallback(() => {
        !participantTeamCredentialsLoading &&
            getParticipantTeamCredentials(participantId);
    }, [participantTeamCredentialsLoading]);

    useEffect(() => {
        participantTeamCredentialsData &&
            setRoleConfigs([
                {
                    id: 'team',
                    name: 'Team',
                    credentials: {
                        accessKeyId: participantTeamCredentialsData.accessKey,
                        secretAccessKey:
                            participantTeamCredentialsData.secretAccessKey,
                        sessionToken:
                            participantTeamCredentialsData.sessionToken,
                    },
                    message: (
                        <>
                            {i18nStrings.event.aboutEnvironmentVariables.p1}{' '}
                            <Link href={links.awsCLIUserGuide} external>
                                {
                                    i18nStrings.event.aboutEnvironmentVariables
                                        .cta
                                }
                            </Link>
                            .
                        </>
                    ),
                },
            ]);
    }, [participantTeamCredentialsData]);

    useEffect(() => {
        participantTeamCredentialsError &&
            showNotification(
                ErrorMessage.getMessage(participantTeamCredentialsError),
                {
                    type: 'error',
                }
            );
    }, [participantTeamCredentialsError]);

    useEffect(() => {
        return () => {
            clearNotification();
        };
    }, []);

    return (
        <>
            <LinkAsync
                onFollow={getTeamCredentials}
                loading={participantTeamCredentialsLoading}>
                {i18nStrings.event.getAWSCliCredentials}
            </LinkAsync>
            {roleConfigs && (
                <AWSAccountAccessModal
                    roleConfigs={roleConfigs}
                    visible={!!roleConfigs}
                    onDismiss={() => setRoleConfigs(undefined)}
                    infoMessage={
                        accessibleRegions &&
                        formatString(
                            i18nStrings.event.awsCredentialsInfoMessage,
                            getStringList(
                                accessibleRegions.map(
                                    (region) => `\`${region}\``
                                ),
                                i18nStrings.and
                            )
                        )
                    }
                    defaultRegion={deploymentRegions?.[0]}
                    size="large"
                />
            )}
        </>
    );
};

export default TeamCredentialsLink;
